
import { supabase } from "@/integrations/supabase/client";
import { config } from "@/config/environment";

export interface SitemapUrl {
  loc: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: number;
}

export class SitemapService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.appUrl;
  }

  /**
   * Generate complete sitemap XML
   */
  async generateSitemap(): Promise<string> {
    const urls: SitemapUrl[] = [];
    
    // Add static routes
    urls.push(...this.getStaticRoutes());
    
    // Add dynamic routes
    const dynamicUrls = await this.getDynamicRoutes();
    urls.push(...dynamicUrls);
    
    return this.generateXML(urls);
  }

  /**
   * Get all static routes with their priorities and change frequencies
   */
  private getStaticRoutes(): SitemapUrl[] {
    const now = new Date().toISOString().split('T')[0];
    
    return [
      {
        loc: `${this.baseUrl}/`,
        lastmod: now,
        changefreq: 'weekly',
        priority: 1.0
      },
      {
        loc: `${this.baseUrl}/about`,
        lastmod: now,
        changefreq: 'weekly',
        priority: 0.8
      },
      {
        loc: `${this.baseUrl}/episodes`,
        lastmod: now,
        changefreq: 'weekly',
        priority: 0.8
      },
      {
        loc: `${this.baseUrl}/characters`,
        lastmod: now,
        changefreq: 'weekly',
        priority: 0.8
      },
      {
        loc: `${this.baseUrl}/activities`,
        lastmod: now,
        changefreq: 'weekly',
        priority: 0.8
      },
      {
        loc: `${this.baseUrl}/culture`,
        lastmod: now,
        changefreq: 'monthly',
        priority: 0.6
      },
      {
        loc: `${this.baseUrl}/partners`,
        lastmod: now,
        changefreq: 'monthly',
        priority: 0.5
      },
      {
        loc: `${this.baseUrl}/contact`,
        lastmod: now,
        changefreq: 'monthly',
        priority: 0.5
      },
      {
        loc: `${this.baseUrl}/legal/privacy`,
        lastmod: now,
        changefreq: 'yearly',
        priority: 0.3
      },
      {
        loc: `${this.baseUrl}/legal/terms`,
        lastmod: now,
        changefreq: 'yearly',
        priority: 0.3
      },
      {
        loc: `${this.baseUrl}/legal/cookies`,
        lastmod: now,
        changefreq: 'yearly',
        priority: 0.3
      }
    ];
  }

  /**
   * Fetch dynamic routes from database
   */
  private async getDynamicRoutes(): Promise<SitemapUrl[]> {
    const urls: SitemapUrl[] = [];
    
    try {
      // Fetch published characters with slugs
      const { data: characters, error: charactersError } = await supabase
        .from('characters')
        .select('slug, updated_at')
        .eq('status', 'published')
        .not('slug', 'is', null);
      
      if (charactersError) {
        console.error('Error fetching characters for sitemap:', charactersError);
      } else if (characters) {
        characters.forEach(character => {
          if (character.slug) {
            urls.push({
              loc: `${this.baseUrl}/characters/${character.slug}`,
              lastmod: character.updated_at ? new Date(character.updated_at).toISOString().split('T')[0] : undefined,
              changefreq: 'monthly',
              priority: 0.6
            });
          }
        });
      }
      
      // Future: Add episodes with detail pages when implemented
      // Future: Add activities with individual pages when implemented
      
    } catch (error) {
      console.error('Error fetching dynamic routes for sitemap:', error);
    }
    
    return urls;
  }

  /**
   * Generate XML sitemap string
   */
  private generateXML(urls: SitemapUrl[]): string {
    const xmlHeader = '<?xml version="1.0" encoding="UTF-8"?>';
    const urlsetOpen = '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">';
    const urlsetClose = '</urlset>';
    
    const urlElements = urls.map(url => {
      let urlXml = `  <url>\n    <loc>${this.escapeXml(url.loc)}</loc>`;
      
      if (url.lastmod) {
        urlXml += `\n    <lastmod>${url.lastmod}</lastmod>`;
      }
      
      if (url.changefreq) {
        urlXml += `\n    <changefreq>${url.changefreq}</changefreq>`;
      }
      
      if (url.priority !== undefined) {
        urlXml += `\n    <priority>${url.priority.toFixed(1)}</priority>`;
      }
      
      urlXml += '\n  </url>';
      return urlXml;
    }).join('\n');
    
    return `${xmlHeader}\n${urlsetOpen}\n${urlElements}\n${urlsetClose}`;
  }

  /**
   * Escape XML special characters
   */
  private escapeXml(unsafe: string): string {
    return unsafe.replace(/[<>&'"]/g, (c) => {
      switch (c) {
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '&': return '&amp;';
        case '\'': return '&apos;';
        case '"': return '&quot;';
        default: return c;
      }
    });
  }

  /**
   * Get sitemap statistics
   */
  async getSitemapStats(): Promise<{
    totalUrls: number;
    staticUrls: number;
    dynamicUrls: number;
    lastGenerated?: string;
  }> {
    const staticUrls = this.getStaticRoutes();
    const dynamicUrls = await this.getDynamicRoutes();
    
    return {
      totalUrls: staticUrls.length + dynamicUrls.length,
      staticUrls: staticUrls.length,
      dynamicUrls: dynamicUrls.length,
      lastGenerated: new Date().toISOString()
    };
  }
}
